import { Link, useParams } from "react-router-dom";
import SiteHeader from "@/components/feature/SiteHeader";
import SiteFooter from "@/components/feature/SiteFooter";
import MediaCard from "@/components/base/MediaCard";
import SpecialCard from "@/components/feature/SpecialCard";
import { usePageMeta } from "@/hooks/usePageMeta";
import { mediaItems } from "@/mocks/media";
import { specialItems } from "@/mocks/specials";
import type { MediaItem } from "@/types/content";

export default function Special() {
  const { id } = useParams<{ id: string }>();
  const special = specialItems.find((item) => item.id === id);
  const others = specialItems.filter((item) => item.id !== id);

  usePageMeta(
    special ? `${special.title} - 影视专题 - 天马传媒` : "专题不存在 - 天马传媒",
    special
      ? `${special.intro.slice(0, 60)}…天马传媒影视专题「${special.title}」，聚合 ${special.mediaIds.length} 部相关作品与推荐理由。`
      : "该影视专题不存在或已下线，返回天马传媒首页继续浏览影视作品与资讯。",
    special ? `${special.title},影视专题,天马传媒,${special.tags.join(",")}` : "影视专题,天马传媒",
  );

  if (!special) {
    return (
      <div className="min-h-screen bg-background-50">
        <SiteHeader />
        <div className="shell flex min-h-[60vh] flex-col items-center justify-center pt-20 text-center">
          <h1 className="font-heading text-2xl font-semibold text-foreground-950">
            专题不存在或已下线
          </h1>
          <p className="mt-2 text-sm text-foreground-600">去专题总览看看我们正在推荐什么。</p>
          <Link
            to="/specials"
            className="mt-6 cursor-pointer whitespace-nowrap rounded-md bg-primary-500 px-5 py-2.5 text-sm text-background-50 transition hover:bg-primary-600"
          >
            返回专题总览
          </Link>
        </div>
        <SiteFooter />
      </div>
    );
  }

  const works = special.mediaIds
    .map((mediaId) => mediaItems.find((item) => item.id === mediaId))
    .filter((item): item is MediaItem => Boolean(item));

  return (
    <div className="min-h-screen bg-background-50">
      <SiteHeader transparent />
      <main>
        <section className="relative overflow-hidden bg-primary-950">
          <div className="absolute inset-0">
            <img
              src={special.cover}
              alt={`${special.title}专题主图`}
              title={`${special.title} - 天马传媒影视专题`}
              className="h-full w-full object-cover object-top"
            />
          </div>
          <div className="absolute inset-0 bg-gradient-to-r from-primary-950/95 via-primary-950/80 to-primary-950/30"></div>
          <div className="relative">
            <div className="shell pb-12 pt-28 md:pb-16 md:pt-36">
              <nav className="flex flex-wrap items-center gap-1.5 text-xs text-background-300" aria-label="面包屑">
                <Link to="/" className="cursor-pointer transition hover:text-accent-300">
                  首页
                </Link>
                <i className="ri-arrow-right-s-line text-background-500"></i>
                <Link to="/specials" className="cursor-pointer transition hover:text-accent-300">
                  影视专题
                </Link>
                <i className="ri-arrow-right-s-line text-background-500"></i>
                <span className="text-background-100">{special.title}</span>
              </nav>

              <p className="mt-6 flex items-center gap-2 text-xs tracking-[0.24em] text-accent-300">
                <span className="h-px w-6 bg-current"></span>
                {special.tags.join(" · ")}
              </p>
              <h1 className="mt-3 max-w-2xl font-heading text-3xl font-bold leading-tight text-background-50 md:text-4xl">
                {special.title}
              </h1>
              <p className="mt-2 text-sm text-background-200 md:text-base">{special.subtitle}</p>
              <p className="mt-5 max-w-2xl text-sm leading-relaxed text-background-300">
                {special.intro}
              </p>
              <div className="mt-6 flex flex-wrap items-center gap-3 text-xs text-background-300">
                <span className="rounded border border-background-50/25 px-2.5 py-1">
                  收录 {works.length} 部作品
                </span>
                <span className="rounded border border-background-50/25 px-2.5 py-1">
                  {special.reasons.length} 条推荐理由
                </span>
              </div>
            </div>
          </div>
        </section>

        <section className="shell py-12 md:py-16">
          <div className="grid gap-4 md:grid-cols-3 md:gap-5">
            {special.reasons.map((reason, index) => (
              <div
                key={reason.title}
                className="rounded-lg border border-background-200 bg-background-50 p-5"
              >
                <span className="font-heading text-2xl font-semibold text-accent-500">
                  {String(index + 1).padStart(2, "0")}
                </span>
                <h2 className="mt-2 font-heading text-base font-semibold text-foreground-950">
                  {reason.title}
                </h2>
                <p className="mt-2 text-sm leading-relaxed text-foreground-600">{reason.text}</p>
              </div>
            ))}
          </div>
        </section>

        <section className="border-y border-background-200 bg-background-100">
          <div className="shell py-12 md:py-16">
            <div className="flex flex-col gap-3 sm:flex-row sm:items-end sm:justify-between">
              <div>
                <p className="mb-2 flex items-center gap-2 text-xs tracking-[0.2em] text-accent-600">
                  <span className="h-px w-6 bg-current"></span>
                  专题片单
                </p>
                <h2 className="font-heading text-xl font-semibold text-foreground-950 md:text-2xl">
                  本专题收录作品
                </h2>
              </div>
              <p className="text-xs text-foreground-500">按专题策划顺序排列</p>
            </div>
            <div className="mt-7 grid grid-cols-2 gap-4 sm:grid-cols-3 md:gap-5 lg:grid-cols-4">
              {works.map((work) => (
                <MediaCard key={work.id} item={work} />
              ))}
            </div>
          </div>
        </section>

        <section className="shell py-12 md:py-16">
          <div className="flex flex-col gap-3 sm:flex-row sm:items-end sm:justify-between">
            <div>
              <p className="mb-2 flex items-center gap-2 text-xs tracking-[0.2em] text-accent-600">
                <span className="h-px w-6 bg-current"></span>
                继续探索
              </p>
              <h2 className="font-heading text-xl font-semibold text-foreground-950 md:text-2xl">
                其他影视专题
              </h2>
            </div>
            <Link
              to="/specials"
              className="cursor-pointer text-sm text-primary-600 transition hover:text-accent-600"
            >
              查看全部专题
            </Link>
          </div>
          <div className="mt-6 grid gap-4 md:grid-cols-2 md:gap-5">
            {others.map((item) => (
              <SpecialCard key={item.id} special={item} />
            ))}
          </div>
          <div className="mt-8 flex flex-wrap items-center gap-3">
            <Link
              to="/"
              className="cursor-pointer whitespace-nowrap rounded-md border border-background-300 bg-background-50 px-4 py-2 text-sm text-foreground-700 transition hover:border-primary-300 hover:text-primary-600"
            >
              返回首页
            </Link>
            <Link
              to="/category/movie"
              className="cursor-pointer whitespace-nowrap rounded-md bg-primary-500 px-4 py-2 text-sm text-background-50 transition hover:bg-primary-600"
            >
              浏览电影库
            </Link>
          </div>
        </section>
      </main>
      <SiteFooter />
    </div>
  );
}